import { useState, useEffect, useRef, useCallback } from 'react';

interface UseTranscribeVoiceInputOptions {
  onTranscript: (text: string) => void;
  idToken: string | null;
}

interface UseTranscribeVoiceInputReturn {
  isListening: boolean;
  isSupported: boolean;
  error: string | null;
  startListening: () => void;
  stopListening: () => void;
}

const SAMPLE_RATE = 16000;
const REGION = 'eu-west-2';

/**
 * Downsample Float32 audio from the device sample rate to the target rate,
 * then convert to Int16 PCM bytes.
 */
function downsampleAndEncodePCM(
  float32: Float32Array,
  inputRate: number,
  outputRate: number,
): ArrayBuffer {
  const ratio = inputRate / outputRate;
  const outputLength = Math.floor(float32.length / ratio);
  const int16 = new Int16Array(outputLength);
  for (let i = 0; i < outputLength; i++) {
    const srcIndex = Math.floor(i * ratio);
    const s = Math.max(-1, Math.min(1, float32[srcIndex]));
    int16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return int16.buffer;
}

export default function useTranscribeVoiceInput({
  onTranscript,
  idToken,
}: UseTranscribeVoiceInputOptions): UseTranscribeVoiceInputReturn {
  const [isListening, setIsListening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const onTranscriptRef = useRef(onTranscript);
  const errorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Mutable refs to hold the active media/transcribe resources
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const audioChunksRef = useRef<ArrayBuffer[]>([]);
  const stopRequestedRef = useRef(false);

  const setErrorWithAutoClear = useCallback((msg: string) => {
    if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    setError(msg);
    errorTimerRef.current = setTimeout(() => setError(null), 5000);
  }, []);

  useEffect(() => {
    onTranscriptRef.current = onTranscript;
  }, [onTranscript]);

  const isSupported =
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia;

  const cleanup = useCallback(() => {
    stopRequestedRef.current = true;

    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current.onaudioprocess = null;
      processorRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((t) => t.stop());
      mediaStreamRef.current = null;
    }
    audioChunksRef.current = [];
  }, []);

  const stopListening = useCallback(() => {
    cleanup();
    setIsListening(false);
  }, [cleanup]);

  const startListening = useCallback(async () => {
    if (!isSupported) return;

    if (!idToken) {
      setErrorWithAutoClear('Please sign in to use voice input');
      return;
    }

    const config = window.__PAILA_CONFIG__;
    const identityPoolId = config?.identityPoolId;
    const userPoolId = config?.userPoolId;

    if (!identityPoolId || !userPoolId) {
      setErrorWithAutoClear('Voice input is not configured');
      return;
    }

    // Stop any existing session
    cleanup();
    stopRequestedRef.current = false;
    setError(null);
    setIsListening(true);

    try {
      // 1. Capture audio
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (micErr: unknown) {
        const name = micErr instanceof DOMException ? micErr.name : '';
        if (name === 'NotAllowedError') {
          setErrorWithAutoClear('Microphone access denied. Please allow microphone access in your browser settings.');
        } else if (name === 'NotFoundError') {
          setErrorWithAutoClear('No microphone found. Please connect a microphone.');
        } else {
          setErrorWithAutoClear('Could not access microphone');
        }
        cleanup();
        setIsListening(false);
        return;
      }
      mediaStreamRef.current = stream;

      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;

      const source = audioContext.createMediaStreamSource(stream);
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;

      // Collect PCM chunks
      audioChunksRef.current = [];
      processor.onaudioprocess = (event) => {
        if (stopRequestedRef.current) return;
        const float32 = event.inputBuffer.getChannelData(0);
        const pcm = downsampleAndEncodePCM(
          float32,
          audioContext.sampleRate,
          SAMPLE_RATE,
        );
        audioChunksRef.current.push(pcm);
      };

      source.connect(processor);
      processor.connect(audioContext.destination);

      // 2. Lazy-load AWS SDK modules
      const [{ TranscribeStreamingClient, StartStreamTranscriptionCommand }, { fromCognitoIdentityPool }] =
        await Promise.all([
          import('@aws-sdk/client-transcribe-streaming'),
          import('@aws-sdk/credential-providers'),
        ]);

      if (stopRequestedRef.current) return;

      // 3. Build Transcribe client with Cognito Identity Pool credentials
      const userPoolProviderUrl = `cognito-idp.${REGION}.amazonaws.com/${userPoolId}`;
      const client = new TranscribeStreamingClient({
        region: REGION,
        credentials: fromCognitoIdentityPool({
          identityPoolId,
          logins: { [userPoolProviderUrl]: idToken },
          clientConfig: { region: REGION },
        }),
      });

      // 4. Async generator that yields audio chunks
      const audioChunks = audioChunksRef.current;
      async function* audioStream() {
        let cursor = 0;
        while (!stopRequestedRef.current) {
          if (cursor < audioChunks.length) {
            const chunk = audioChunks[cursor];
            cursor++;
            yield { AudioEvent: { AudioChunk: new Uint8Array(chunk) } };
          } else {
            // Wait for more audio data
            await new Promise((r) => setTimeout(r, 50));
          }
        }
        // Yield remaining chunks after stop
        while (cursor < audioChunks.length) {
          const chunk = audioChunks[cursor];
          cursor++;
          yield { AudioEvent: { AudioChunk: new Uint8Array(chunk) } };
        }
      }

      // 5. Start transcription
      let response;
      try {
        const command = new StartStreamTranscriptionCommand({
          LanguageCode: 'en-GB',
          MediaEncoding: 'pcm',
          MediaSampleRateHertz: SAMPLE_RATE,
          AudioStream: audioStream(),
        });
        response = await client.send(command);
      } catch (transcribeErr: unknown) {
        console.error('Voice input: Transcribe connection error', transcribeErr);
        const message = transcribeErr instanceof Error ? transcribeErr.message : '';
        if (message.includes('credential') || message.includes('token') || message.includes('auth') || message.includes('Access Denied')) {
          setErrorWithAutoClear('Voice service authentication failed');
        } else {
          setErrorWithAutoClear('Could not connect to voice service');
        }
        cleanup();
        setIsListening(false);
        return;
      }

      if (stopRequestedRef.current || !response.TranscriptResultStream) return;

      // 6. Read transcript results
      for await (const event of response.TranscriptResultStream) {
        if (stopRequestedRef.current) break;

        const results = event.TranscriptEvent?.Transcript?.Results;
        if (!results?.length) continue;

        for (const result of results) {
          if (result.IsPartial) continue;
          const transcript = result.Alternatives?.[0]?.Transcript;
          if (transcript) {
            onTranscriptRef.current(transcript.trim());
          }
        }
      }
    } catch (err) {
      console.error('Voice input: transcription error', err);
      setErrorWithAutoClear('Voice input encountered an error');
      cleanup();
      setIsListening(false);
    }
  }, [isSupported, idToken, cleanup, setErrorWithAutoClear]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
      if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    };
  }, [cleanup]);

  return { isListening, isSupported, error, startListening, stopListening };
}
